import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import {
  useCheckNewInsightsAvailableQuery,
  useUpdateInsightsNotificationStatusMutation,
} from '__generated__/api-types-and-hooks'

export const useNewInsightsPopup = () => {
  const [showPopup, setShowPopup] = useState(false)

  // Use real API response to control popup visibility
  const userProfile = useSelector((state: RootState) => state.user?.userProfile)
  const userFromState = useSelector((state: RootState) => state.user?.user)
  const userId = userProfile?.id || userFromState?.id
  const tenantId = userProfile?.tenantId?.[0] || userFromState?.tenantId?.[0]

  const { data: insightsCheck } = useCheckNewInsightsAvailableQuery(
    {
      userId,
      tenantId,
    },
    {
      enabled: !!userId && !!tenantId,
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnMount: true, // Refetch on component mount (page navigation)
      refetchInterval: 5 * 60 * 1000, // Poll every 5 minutes to check for 10 AM CST
    }
  )

  useEffect(() => {
    if (!insightsCheck?.checkNewInsightsAvailable) {
      setShowPopup(false)
      return
    }

    const { shouldShowPopup, lastInsightGeneratedAt } = insightsCheck.checkNewInsightsAvailable

    // Check if popup was already shown in this browser session (prevents showing multiple times on same page)
    const hasShownPopupThisPageLoad = sessionStorage.getItem('insightsPopupShown') === 'true'

    // CRITICAL: Check if we've EVER shown popup for these specific insights (persists across signouts)
    const lastShownInsightTimestamp = localStorage.getItem('lastShownInsightTimestamp')

    // Determine if these are the same insights we've already shown
    let alreadyShownTheseInsights = false
    if (lastShownInsightTimestamp && lastInsightGeneratedAt) {
      if (lastShownInsightTimestamp === lastInsightGeneratedAt) {
        // Same insights - already shown
        alreadyShownTheseInsights = true
      } else {
        // Different insights - NEW insights available!
        alreadyShownTheseInsights = false
      }
    }

    // Only show popup if:
    // 1. Backend says we should show it
    // 2. We haven't shown it yet in this page load
    // 3. We haven't EVER shown popup for these specific insights (even across signouts!)
    if (shouldShowPopup && !hasShownPopupThisPageLoad && !alreadyShownTheseInsights) {
      setShowPopup(true)
      sessionStorage.setItem('insightsPopupShown', 'true') // Prevent showing multiple times on same page
      if (lastInsightGeneratedAt) {
        // Store in localStorage so it persists across signouts!
        localStorage.setItem('lastShownInsightTimestamp', lastInsightGeneratedAt)
      }
    } else {
      // Hide popup if conditions no longer met
      setShowPopup(false)
    }
  }, [insightsCheck])

  const { mutate: updateNotificationStatus } = useUpdateInsightsNotificationStatusMutation()

  const handleClose = () => {
    setShowPopup(false)

    // Update lastNewInsightsPopupShownAt to track that user has been shown the popup
    if (userId && tenantId) {
      const timestamp = new Date().toISOString()

      updateNotificationStatus(
        {
          input: {
            userId,
            tenantId,
            lastNewInsightsPopupShownAt: timestamp,
          },
        },
        {
          onSuccess: () => {
            // Successfully updated
          },
          onError: () => {
            // Failed to update
          },
        }
      )
    }
  }

  const handleViewInsights = () => {
    // Update lastNewInsightsViewedAt when user clicks to view insights
    if (userId && tenantId) {
      updateNotificationStatus({
        input: {
          userId,
          tenantId,
          lastNewInsightsViewedAt: new Date().toISOString(),
        },
      })
    }
    setShowPopup(false)
  }

  return {
    showPopup,
    insightsCount: insightsCheck?.checkNewInsightsAvailable?.insightsCount || 0,
    handleClose,
    handleViewInsights,
  }
}
